import type { WaterDataRow } from '../../utils/waterTable'

// WaterLog v0 PRD §3/§11: the four v0 parameters and their range-validation
// bounds. Mirrors the server-enforced bounds in WaterLogService.ts exactly
// (temperatureC/ph/dissolvedOxygenMgL/salinityPpt decimalField calls) so
// the client never invents a separate threshold that could drift from the
// server contract — see PRD §10 risk #6.
export type WaterCaptureParameter = 'temperature' | 'ph' | 'dissolvedOxygen' | 'salinity' | 'flow'

export const WATER_CAPTURE_PARAMETERS: WaterCaptureParameter[] = ['temperature', 'ph', 'dissolvedOxygen', 'salinity']

export type WaterCaptureParameterConfig = {
  min: number
  max: number
  unit: string
}

export const WATER_CAPTURE_PARAMETER_RANGES: Record<WaterCaptureParameter, WaterCaptureParameterConfig> = {
  temperature: { min: 0, max: 40, unit: '°C' },
  ph: { min: 4, max: 10, unit: '' },
  dissolvedOxygen: { min: 0, max: 20, unit: 'mg/L' },
  salinity: { min: 0, max: 45, unit: 'ppt' },
  // Hatchery v0 (#84): flow is not a WaterLog column — it travels in
  // `metadata.flowLpm`, so the server has no bound to mirror.
  flow: { min: 0.01, max: 5000, unit: 'L/min' }
}

/** A row's value for a parameter (flow lives in metadata). */
export const readWaterParameterValue = (row: WaterDataRow, parameter: WaterCaptureParameter): number | null => {
  if (parameter === 'flow') {
    const flow = (row.metadata as Record<string, unknown> | undefined)?.flowLpm
    return typeof flow === 'number' && Number.isFinite(flow) ? flow : null
  }
  const value = row[parameter]
  return value === null || value === undefined ? null : value
}

export type WaterCaptureValueValidation =
  | { ok: true }
  | { ok: false; reason: 'invalid' | 'out-of-range' }

export const validateWaterCaptureValue = (
  parameter: WaterCaptureParameter,
  rawValue: string
): WaterCaptureValueValidation => {
  const trimmed = rawValue.trim()
  if (trimmed.length === 0) return { ok: false, reason: 'invalid' }

  const value = Number(trimmed.replace(',', '.'))
  if (!Number.isFinite(value)) return { ok: false, reason: 'invalid' }

  const range = WATER_CAPTURE_PARAMETER_RANGES[parameter]
  if (value < range.min || value > range.max) return { ok: false, reason: 'out-of-range' }

  return { ok: true }
}

// PRD §11 AC #9 / §10 risk #5: "reasonable duplicate blocking" (strategy
// doc §10 P0 table) for a single live capture — warn, never hard-block, on
// a same-production-unit, same-parameter reading within 15 minutes. This
// is a different check from the CSV-import (lot, day) dedup warnings
// shipped for Feed/Mortality earlier this session: those compare
// already-typed batch rows against a day-granularity key; this compares a
// single live capture against existing rows at minute granularity.
//
// `existingRows` is expected to already be scoped to the target
// production unit — callers load it via `loadWaterTableRows({ farmId,
// productionUnitId })`, which is already PU-scoped, so there is no
// separate productionUnitId to match here.
export const DUPLICATE_WARNING_WINDOW_MS = 15 * 60 * 1000

export const findLikelyDuplicateReading = (args: {
  parameter: WaterCaptureParameter
  recordedAtIso: string
  existingRows: WaterDataRow[]
}): WaterDataRow | null => {
  const candidateMs = Date.parse(args.recordedAtIso)
  if (!Number.isFinite(candidateMs)) return null

  for (const row of args.existingRows) {
    if (readWaterParameterValue(row, args.parameter) === null) continue

    const existingMs = Date.parse(row.date)
    if (!Number.isFinite(existingMs)) continue

    if (Math.abs(existingMs - candidateMs) <= DUPLICATE_WARNING_WINDOW_MS) {
      return row
    }
  }

  return null
}

/**
 * Builds the WaterDataRow for a single Crew Mode capture. Never sets
 * `lotId` (PRD §4: "No lot exposure" — WaterLog is PU-scoped, D-2; the
 * server rejects any WaterLog payload carrying a `lotId` key). Always
 * stamps `capturedByActorId` from the active operator session (Charter
 * D-4) rather than leaving it for the server's device-fallback.
 *
 * `lot` (the plain-text label, optional) is the one exception to "no lot
 * exposure": the biomass/SFR projection engine (`normalizeProjectionWaterLogs`
 * in mortalityProjection.ts) hard-filters out any water reading with an
 * empty `lot`, so a reading captured with none is silently invisible to
 * every downstream projection even though it's stored correctly — the bug
 * the user hit 2026-09-16. The caller resolves this from the pond's
 * currently active lot when there's exactly one (WaterLogCapture.tsx,
 * mirroring the same pondLotResolution.ts helper Mortality/WeightSample
 * use) and leaves it unset otherwise; this function never derives it
 * itself. Pure and independently testable — PRD AC #8 requires a test
 * asserting the payload shape, not just visual inspection.
 */
export const buildWaterCaptureRow = (args: {
  id: string
  parameter: WaterCaptureParameter
  numericValue: number
  recordedAtIso: string
  capturedByActorId: string | undefined
  lot?: string
}): WaterDataRow => ({
  id: args.id,
  date: args.recordedAtIso,
  lot: args.lot ?? '',
  temperature: args.parameter === 'temperature' ? args.numericValue : null,
  ph: args.parameter === 'ph' ? args.numericValue : null,
  dissolvedOxygen: args.parameter === 'dissolvedOxygen' ? args.numericValue : null,
  dissolvedOxygenSaturation: null,
  manualDissolvedOxygen: null,
  manualDissolvedOxygenSaturation: null,
  salinity: args.parameter === 'salinity' ? args.numericValue : null,
  notes: '',
  metadata: args.parameter === 'flow' ? { sourceType: 'manual', flowLpm: args.numericValue } : { sourceType: 'manual' },
  capturedByActorId: args.capturedByActorId
})
